import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Award, Briefcase, LogOut, Plus, Search as SearchIcon, Send, X, MapPin, ShieldCheck } from "lucide-react";
import PremiumSearch from "@/components/PremiumSearch";

const TIER_STYLE = {
  Platinum: "text-[#8b5cf6] border-[#8b5cf6]",
  Diamond:  "text-[#06b6d4] border-[#06b6d4]",
  Gold:     "text-[#F4B400] border-[#F4B400]",
  Silver:   "text-[#9AA0A6] border-[#9AA0A6]",
  Bronze:   "text-[#CD7F32] border-[#CD7F32]",
};

// Countries we let recruiters filter by — covers the main KSA-hire labour
// flows. Picked from the country list /hire offers as a hero filter.
const COUNTRY_OPTIONS = [
  "Saudi Arabia", "United Arab Emirates", "Qatar", "Kuwait", "Bahrain", "Oman",
  "Egypt", "Jordan", "Lebanon", "Syria", "Morocco", "Tunisia",
  "France", "Germany", "Italy", "United Kingdom", "Romania",
  "India", "Pakistan", "Bangladesh", "Philippines", "Sri Lanka", "Nepal",
];

export default function RecruiterDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Country can be pre-set via ?country=… (from /hire hero picker).
  const initialCountry = searchParams.get("country") || "";
  const [filters, setFilters] = useState({
    role: "",
    city: "",
    country: initialCountry,
    min_experience: 0,
    nitaqat_min: 0,
    badge_tier: "",
    is_saudi: "",
  });
  const [cities, setCities] = useState([]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(null);
  const [intro, setIntro] = useState({ message: "" });
  // "candidates" = organic Maqam users, "premium" = agency-curated list
  // If recruiter arrives with a country picked on /hire that's outside KSA,
  // open the Premium (agency) tab by default — that's where overseas talent lives.
  const initialTab = initialCountry && initialCountry !== "Saudi Arabia" ? "premium" : "candidates";
  const [tab, setTab] = useState(initialTab);

  useEffect(() => {
    api.get("/jobs/cities").then((r) => setCities(r.data)).catch(() => {});
    runSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runSearch = async () => {
    setLoading(true);
    const params = {};
    if (filters.role) params.role = filters.role;
    if (filters.city) params.city = filters.city;
    if (filters.country) params.country = filters.country;
    if (filters.min_experience) params.min_experience = filters.min_experience;
    if (filters.nitaqat_min) params.nitaqat_min = filters.nitaqat_min;
    if (filters.badge_tier) params.badge_tier = filters.badge_tier;
    if (filters.is_saudi !== "") params.is_saudi = filters.is_saudi;
    try {
      const r = await api.get("/recruiter/candidates", { params });
      setResults(r.data);
    } catch (err) {
      if (err?.response?.status === 403) {
        toast.error("Recruiter account required");
        navigate("/");
      }
    } finally {
      setLoading(false);
    }
  };

  const openCandidate = async (id) => {
    try {
      const r = await api.get(`/recruiter/candidates/${id}`);
      setActive(r.data);
      setIntro({ message: `Hi, we came across your Maqam profile and would love to connect about an opportunity at ${user.full_name ? "our company" : "our company"}. Are you open to a short chat this week?` });
    } catch {
      toast.error("Could not load candidate");
    }
  };

  const sendIntro = async () => {
    if (!active) return;
    try {
      await api.post("/recruiter/intro", { candidate_id: active.candidate_id, message: intro.message });
      toast.success(`Intro request sent to ${active.display_name}`);
      setActive(null);
    } catch {
      toast.error("Could not send intro");
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-[#0c0c0d] text-white">
      <header className="bg-[#141416] text-white border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
          <Link to="/app/dashboard" className="flex items-center gap-3 group" title="Back to app" data-testid="rec-back-home">
            <div className="w-8 h-8 bg-white text-black flex items-center justify-center font-display font-black group-hover:bg-white/85 transition-colors">M</div>
            <div>
              <div className="font-display font-bold group-hover:opacity-80 transition-opacity">Maqam · Recruiter</div>
              <div className="font-mono text-[10px] text-white/50 uppercase tracking-widest">
                {user?.company_name || user?.full_name}
              </div>
            </div>
          </Link>
          <div className="flex items-center gap-2">
            <Link to="/app/dashboard" className="hidden sm:flex items-center gap-2 text-sm font-bold text-white/70 hover:text-white hover:bg-white/10 px-4 py-2 transition-colors" data-testid="rec-back-app">
              <ArrowLeft className="w-4 h-4" /> Back to app
            </Link>
            <button onClick={handleLogout} className="flex items-center gap-2 text-sm font-bold hover:bg-white/10 px-4 py-2" data-testid="rec-logout">
              <LogOut className="w-4 h-4" /> Log out
            </button>
          </div>
        </div>
        {/* Tab bar: Maqam candidate pool vs agency-curated Premium list */}
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 flex items-center gap-1">
          <button
            onClick={() => setTab("candidates")}
            className={`px-5 py-3 text-xs font-mono uppercase tracking-widest transition-colors ${tab === "candidates" ? "bg-white text-black" : "text-white/70 hover:text-white"}`}
            data-testid="rec-tab-candidates"
          >
            Candidate pool · {results.length}
          </button>
          <button
            onClick={() => setTab("premium")}
            className={`px-5 py-3 text-xs font-mono uppercase tracking-widest transition-colors flex items-center gap-2 ${tab === "premium" ? "bg-[#FF9500] text-black" : "text-white/70 hover:text-white"}`}
            data-testid="rec-tab-premium"
          >
            ⭐ Premium workers
          </button>
        </div>
      </header>

      {tab === "candidates" ? (
      <main className="max-w-[1400px] mx-auto px-6 md:px-10 py-8 grid lg:grid-cols-[320px_1fr] gap-6">
        {/* Filters */}
        <aside className="bg-[#141416] border border-white/10 p-5 h-fit lg:sticky lg:top-6" data-testid="rec-filters">
          <h2 className="font-display font-bold text-lg mb-4 flex items-center gap-2">
            <SearchIcon className="w-4 h-4" /> Filter candidates
          </h2>
          <div className="space-y-4 text-sm">
            <label className="block">
              <span className="block font-mono text-[10px] uppercase tracking-widest text-white/50 mb-1">Role / skill</span>
              <input
                value={filters.role}
                onChange={(e) => setFilters({ ...filters, role: e.target.value })}
                placeholder="e.g. backend"
                className="w-full bg-transparent text-white placeholder-white/30 border-b border-white/15 focus:border-white outline-none py-2"
                data-testid="filter-role"
              />
            </label>
            <label className="block">
              <span className="block font-mono text-[10px] uppercase tracking-widest text-white/50 mb-1">Country</span>
              <select
                value={filters.country}
                onChange={(e) => setFilters({ ...filters, country: e.target.value })}
                className="w-full bg-transparent text-white border-b border-white/15 focus:border-white outline-none py-2"
                data-testid="filter-country"
              >
                <option value="" className="bg-[#141416]">Any</option>
                {COUNTRY_OPTIONS.map((c) => (
                  <option key={c} value={c} className="bg-[#141416]">{c}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="block font-mono text-[10px] uppercase tracking-widest text-white/50 mb-1">City</span>
              <select value={filters.city} onChange={(e) => setFilters({ ...filters, city: e.target.value })} className="w-full bg-transparent text-white border-b border-white/15 focus:border-white outline-none py-2" data-testid="filter-city">
                <option value="" className="bg-[#141416]">Any</option>
                {cities.map((c) => <option key={c} className="bg-[#141416]">{c}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="block font-mono text-[10px] uppercase tracking-widest text-white/50 mb-1">Min experience (years)</span>
              <input type="number" min={0} value={filters.min_experience} onChange={(e) => setFilters({ ...filters, min_experience: e.target.value })} className="w-full bg-transparent text-white border-b border-white/15 focus:border-white outline-none py-2" data-testid="filter-exp" />
            </label>
            <label className="block">
              <span className="block font-mono text-[10px] uppercase tracking-widest text-white/50 mb-1">Min Nitaqat score</span>
              <input type="number" min={0} max={100} value={filters.nitaqat_min} onChange={(e) => setFilters({ ...filters, nitaqat_min: e.target.value })} className="w-full bg-transparent text-white border-b border-white/15 focus:border-white outline-none py-2" data-testid="filter-nitaqat" />
            </label>
            <label className="block">
              <span className="block font-mono text-[10px] uppercase tracking-widest text-white/50 mb-1">Minimum badge tier</span>
              <select value={filters.badge_tier} onChange={(e) => setFilters({ ...filters, badge_tier: e.target.value })} className="w-full bg-transparent text-white border-b border-white/15 focus:border-white outline-none py-2" data-testid="filter-badge">
                <option value="" className="bg-[#141416]">Any</option>
                <option value="Bronze" className="bg-[#141416]">Bronze+</option>
                <option value="Silver" className="bg-[#141416]">Silver+</option>
                <option value="Gold" className="bg-[#141416]">Gold+</option>
                <option value="Diamond" className="bg-[#141416]">Diamond+</option>
                <option value="Platinum" className="bg-[#141416]">Platinum only (Elite)</option>
              </select>
            </label>
            <label className="block">
              <span className="block font-mono text-[10px] uppercase tracking-widest text-white/50 mb-1">Saudi nationality</span>
              <select value={filters.is_saudi} onChange={(e) => setFilters({ ...filters, is_saudi: e.target.value })} className="w-full bg-transparent text-white border-b border-white/15 focus:border-white outline-none py-2">
                <option value="" className="bg-[#141416]">Any</option>
                <option value="true" className="bg-[#141416]">Saudi only</option>
                <option value="false" className="bg-[#141416]">Non-Saudi only</option>
              </select>
            </label>
            <button onClick={runSearch} disabled={loading} className="w-full bg-white text-black hover:bg-white/85 font-bold py-3 transition-colors disabled:opacity-60" data-testid="filter-apply">
              {loading ? "Searching…" : "Apply filters"}
            </button>
          </div>
        </aside>

        {/* Results */}
        <section>
          <div className="mb-4 flex items-center justify-between">
            <h1 className="font-display font-black text-3xl tracking-tighter">
              {results.length} candidate{results.length === 1 ? "" : "s"}
            </h1>
            <div className="font-mono text-xs text-white/50 uppercase tracking-widest">Anonymized · tap to view full profile</div>
          </div>
          {results.length === 0 ? (
            <div className="border border-dashed border-white/15 p-16 text-center text-white/55">No candidates match your filters.</div>
          ) : (
            <div className="space-y-3" data-testid="candidates-list">
              {results.map((c) => (
                <div
                  key={c.candidate_id}
                  onClick={() => openCandidate(c.candidate_id)}
                  className="bg-[#141416] border border-white/10 hover:border-white/40 p-5 cursor-pointer transition-colors"
                  data-testid={`candidate-${c.candidate_id}`}
                >
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 bg-white/[0.06] border border-white/15 flex items-center justify-center font-display font-bold">
                          {c.display_name.split(" ").map((x) => x[0]).join("").slice(0,2)}
                        </div>
                        <div>
                          <div className="font-display font-bold text-lg">{c.display_name}</div>
                          <div className="text-xs text-white/55">
                            {c.headline || (c.job_titles || []).join(" · ")}
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-3 text-xs text-white/70">
                        <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {(c.preferred_cities || []).join(", ") || "—"}</span>
                        <span>· {c.experience_years || 0} yrs</span>
                        {c.is_saudi && <span className="px-2 py-0.5 bg-[#34C759]/15 text-[#34C759] font-mono text-[10px] uppercase tracking-widest border border-[#34C759]/30">Saudi</span>}
                      </div>
                      {c.skills?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-3">
                          {c.skills.slice(0, 8).map((s) => (
                            <span key={s} className="px-2 py-0.5 bg-white/[0.04] text-xs border border-white/10 text-white/70">{s}</span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="text-end flex-shrink-0 flex items-start gap-4">
                      <div>
                        <div className="font-mono text-[10px] uppercase tracking-widest text-white/50">Maqam</div>
                        <div className={`num-ticker font-display font-black text-3xl ${c.maqam_score >= 85 ? "text-[#34C759]" : c.maqam_score >= 70 ? "text-[#06b6d4]" : c.maqam_score >= 50 ? "text-[#F4B400]" : "text-white/40"}`} title={`Skills ${c.maqam_breakdown?.skill_score} · Interview ${c.maqam_breakdown?.interview_score} · Profile ${c.maqam_breakdown?.profile_score} · CV ${c.maqam_breakdown?.cv_score}`}>
                          {c.maqam_score}
                        </div>
                        {c.looking_for_work && (
                          <div className="mt-1 text-[9px] font-mono uppercase tracking-widest text-[#FF9500]">🔥 Active</div>
                        )}
                      </div>
                      <div>
                        <div className="font-mono text-[10px] uppercase tracking-widest text-white/50">Nitaqat</div>
                        <div className={`num-ticker font-display font-black text-2xl ${c.nitaqat_score >= 85 ? "text-[#8b5cf6]" : c.nitaqat_score >= 65 ? "text-[#F4B400]" : c.nitaqat_score >= 45 ? "text-[#9AA0A6]" : "text-white/40"}`}>
                          {c.nitaqat_score}
                        </div>
                      </div>
                    </div>
                  </div>
                  {c.badges?.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-white/10 flex flex-wrap gap-2">
                      {c.badges.map((b) => (
                        <div key={`${c.candidate_id}-${b.skill}-${b.tier}`} className={`inline-flex items-center gap-2 px-2.5 py-1 border ${TIER_STYLE[b.tier] || "border-white/20"} bg-black/30`}>
                          <Award className={`w-3.5 h-3.5 ${TIER_STYLE[b.tier]?.split(" ")[0] || ""}`} />
                          <span className="font-mono text-[11px] font-bold">{b.tier}</span>
                          <span className="text-xs text-white/80">{b.skill}</span>
                          {b.difficulty === "hard" && <span className="text-[9px] font-mono text-[#8b5cf6]">HARD</span>}
                          {b.integrity_score === 100 && <ShieldCheck className="w-3 h-3 text-[#34C759]" />}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
      ) : (
      <PremiumSearch />
      )}

      {/* Candidate detail + intro */}
      {active && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur-sm p-4" data-testid="candidate-modal">
          <div className="bg-[#141416] text-white w-full max-w-3xl border border-white/15 max-h-[92vh] flex flex-col">
            <div className="flex items-start justify-between p-6 border-b border-white/10">
              <div>
                <div className="font-mono text-xs uppercase tracking-widest text-white/50 mb-1">Candidate</div>
                <h3 className="font-display font-black text-3xl tracking-tighter">{active.display_name}</h3>
                <div className="text-white/70 text-sm mt-1">{active.headline}</div>
                <div className="text-xs text-white/50 mt-1">{active.experience_years} years experience · {(active.preferred_cities || []).join(", ")}</div>
              </div>
              <button onClick={() => setActive(null)} className="p-1 hover:bg-white/10">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-5">
              {active.badges?.length > 0 && (
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-white/50 mb-2">Verified skills</div>
                  <div className="flex flex-wrap gap-2">
                    {active.badges.map((b) => (
                      <div key={`detail-${b.skill}-${b.tier}`} className={`inline-flex items-center gap-2 px-3 py-2 border ${TIER_STYLE[b.tier]} bg-black/30`}>
                        <Award className={`w-4 h-4 ${TIER_STYLE[b.tier]?.split(" ")[0]}`} />
                        <div>
                          <div className="font-bold text-sm">{b.tier} · {b.skill}</div>
                          <div className="text-[10px] font-mono text-white/55">
                            {b.score}% · {b.difficulty?.toUpperCase()} · Integrity {b.integrity_score}/100
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {active.skills?.length > 0 && (
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-white/50 mb-2">All skills</div>
                  <div className="flex flex-wrap gap-1">
                    {active.skills.map((s) => (
                      <span key={s} className="px-2 py-1 bg-white/[0.04] text-xs border border-white/10 text-white/80">{s}</span>
                    ))}
                  </div>
                </div>
              )}
              {active.cv_text_preview && (
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-white/50 mb-2">CV preview</div>
                  <pre className="whitespace-pre-wrap text-sm text-white/80 bg-black/40 p-4 border border-white/10 max-h-60 overflow-y-auto">
                    {active.cv_text_preview}
                  </pre>
                </div>
              )}
              <div className="border-t border-white/10 pt-5">
                <div className="font-display font-bold text-lg mb-3">Send intro request</div>
                <textarea
                  value={intro.message}
                  onChange={(e) => setIntro({ message: e.target.value })}
                  rows={4}
                  className="w-full bg-black/40 border border-white/15 p-3 focus:border-white outline-none text-sm text-white placeholder-white/30"
                  data-testid="intro-message"
                />
                <button onClick={sendIntro} className="mt-3 bg-white text-black hover:bg-white/85 font-bold px-6 py-3 flex items-center gap-2 transition-colors" data-testid="intro-send">
                  <Send className="w-4 h-4" /> Send to candidate's private inbox
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
